import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { TransactionModal } from "../../widgets/transaction"
import { UserModal } from "../../widgets/user"

/* Helpers */
import { withRouter } from "../../helpers"

/* Shared */
import { KINDS, kindOf, statusOf, PLANS, PERIODS, periodOf, cardOf, shortMoney } from "../../widgets/transaction/shared"
import { organizationIcon } from "../../widgets/organization/shared"
import { plural } from "../../widgets/category/shared"
import { money } from "../../widgets/service/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list } from "../../api/Transaction"

/* Moment */
import moment from "moment"


/* The sources of the money */
const SOURCES = [
    { key: "", text: "Все" },
    { key: "subscription", text: "Подписки" },
    { key: "certificate", text: "Сертификаты" }
]

/* The orders of the list */
const SORTS = [
    { key: "new", text: "Новые" },
    { key: "old", text: "Старые" },
    { key: "amount", text: "Крупные" }
]

/* The empty filters */
const INITIAL = { search: "", period: "month", source: "", kind: "", status: "", sort: "new" }


/* Page Transactions — the money of the platform: what the points pay for Bookify Pro and what the
   clients pay for the gift certificates. The period leads the page, the list and the numbers follow it */
class Transactions extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            sum: 0,
            limit: 30,
            page: 1,
            statistics: null,
            kinds: [],
            series: null,
            subscriptions: null,
            ...INITIAL
        }

        this._transaction = createRef()
        this._user = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, period, source, kind, status, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        list({ page, search, source, kind, status, sort, ...periodOf(period) }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    sum: response.data.sum,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    kinds: response.data.kinds,
                    series: response.data.series,
                    subscriptions: response.data.subscriptions
                })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Every filter starts from the first page */
    refilter = fields => {
        this.setState(fields, () => this.load(1, true))
    }

    /* The search waits for the hand to stop */
    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(1, true), text.length === 0 ? 0 : 450)
    }

    /* A filter pressed twice lets go */
    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Транзакции</div>

                <div className="organization-header-tools">
                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(1, true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Точка, заказ, покупатель или карта" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>
                </div>

            </div>
        )
    }

    /* The periods over the whole page */
    _periods = () => {

        /* Fields */
        const { period } = this.state

        return (
            <div className="transaction-periods">
                {PERIODS.map(item =>
                    <div key={item.key} onClick={() => this.refilter({ period: item.key })} className={period === item.key ? "active" : ""}>{item.text}</div>
                )}
            </div>
        )
    }

    /* The buckets of the chart — every day (or month) of the period, the silent ones too */
    buckets = () => {

        /* Fields */
        const { series, period } = this.state

        if (!series || series.list.length === 0) {
            return []
        }

        const unit = series.unit
        const { from, to } = periodOf(period)
        const start = from ? moment(from) : moment(series.list[0].day)
        const end = to ? moment(to) : moment()
        const result = []

        const cursor = moment(start).startOf(unit)

        while (cursor.isSameOrBefore(end, unit) && result.length < 400) {

            const key = unit === "month" ? cursor.format("YYYY-MM-01") : cursor.format("YYYY-MM-DD")
            const row = series.list.find(item => moment(item.day).format(unit === "month" ? "YYYY-MM-01" : "YYYY-MM-DD") === key)

            result.push({
                key,
                text: unit === "month" ? cursor.format("MMM YY") : cursor.format("D MMM"),
                subscriptions: row ? row.subscriptions : 0,
                certificates: row ? row.certificates : 0
            })

            cursor.add(1, unit)
        }

        return result
    }

    /* The money of the period — the big number, the trend and the chart */
    _summary = () => {

        /* Fields */
        const { statistics, subscriptions, status: filter, source } = this.state

        if (!statistics) {
            return null
        }

        const buckets = this.buckets()
        const top = Math.max(...buckets.map(item => item.subscriptions + item.certificates), 1)
        const average = statistics.success > 0 ? Math.round(statistics.revenue / statistics.success) : 0

        let trend = null

        if (statistics.previous) {

            if (statistics.previous.revenue > 0) {
                trend = Math.round((statistics.revenue - statistics.previous.revenue) / statistics.previous.revenue * 100)
            }
            else if (statistics.revenue > 0) {
                trend = 100
            }
        }

        const tiles = [
            { key: "subscription", field: "source", icon: "card", value: money(statistics.subscriptions), text: "подписки точек", accent: "blue", active: source === "subscription", empty: "" },
            { key: "certificate", field: "source", icon: "gift", value: money(statistics.certificates), text: "сертификаты", accent: "pink", active: source === "certificate", empty: "" },
            { key: "failed", field: "status", icon: "close", value: statistics.failed.toLocaleString("ru-RU"), text: statistics.lost > 0 ? `отказов · ${shortMoney(statistics.lost)} ₸` : "отказов шлюза", accent: "red", active: filter === "failed", empty: "" },
            { key: "pending", field: "status", icon: "clock", value: statistics.pending.toLocaleString("ru-RU"), text: "не довели оплату", accent: "orange", active: filter === "pending", empty: "" }
        ]

        return (
            <div className="transaction-summary">

                <div className="transaction-revenue">

                    <div className="transaction-revenue-top">
                        <div>
                            <span>Поступило за период</span>
                            <b>{money(statistics.revenue)}</b>
                            <em>
                                {statistics.success} {plural(statistics.success, "оплата", "оплаты", "оплат")}
                                {average > 0 ? ` · средний чек ${money(average)}` : ""}
                                {statistics.payers > 0 ? ` · ${statistics.payers} ${plural(statistics.payers, "точка", "точки", "точек")}` : ""}
                            </em>
                        </div>
                        {trend !== null &&
                            <div className={`transaction-trend ${trend >= 0 ? "up" : "down"}`} title={`Прошлый такой же период: ${money(statistics.previous.revenue)}`}>
                                <Icon name={trend >= 0 ? "up" : "down"} size={13} width={2.6} />
                                {trend >= 0 ? "+" : ""}{trend}%
                            </div>
                        }
                    </div>

                    <div className="transaction-chart">
                        {buckets.length === 0 && <div className="transaction-chart-empty">За период денег не было</div>}
                        {buckets.map(item => {

                            const total = item.subscriptions + item.certificates

                            return (
                                <div key={item.key} className="transaction-chart-column" title={`${item.text}: ${money(total)}`}>
                                    <div className="transaction-chart-stack" style={{ height: `${total > 0 ? Math.max(4, total / top * 100) : 0}%` }}>
                                        <i className="certificates" style={{ flex: item.certificates }} />
                                        <i className="subscriptions" style={{ flex: item.subscriptions }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {buckets.length > 0 &&
                        <div className="transaction-chart-axis">
                            <span>{buckets[0].text}</span>
                            <span>{buckets[buckets.length - 1].text}</span>
                        </div>
                    }

                </div>

                <div className="transaction-side">

                    <div className="transaction-tiles">
                        {tiles.map(tile =>
                            <div
                                key={tile.key}
                                onClick={() => this.toggle(tile.field, tile.key, tile.empty)}
                                className={`user-tile ${tile.accent} clickable ${tile.active ? "active" : ""}`}
                            >
                                <div className="user-tile-icon"><Icon name={tile.icon} size={16} width={2} /></div>
                                <b>{tile.value}</b>
                                <span>{tile.text}</span>
                            </div>
                        )}
                    </div>

                    {subscriptions &&
                        <div className="transaction-now">
                            <div className="transaction-now-title">Подписки сейчас</div>
                            <div className="transaction-now-row">
                                <div><b>{subscriptions.active}</b><span>активных</span></div>
                                <div><b>{shortMoney(subscriptions.monthly)} ₸</b><span>в месяц</span></div>
                                <div><b>{subscriptions.renewing}</b><span>продлятся за 7 дней{subscriptions.renewingAmount > 0 ? ` · ${shortMoney(subscriptions.renewingAmount)} ₸` : ""}</span></div>
                                <div className={subscriptions.pastDue > 0 ? "alarm" : ""}><b>{subscriptions.pastDue}</b><span>не оплачены</span></div>
                            </div>
                        </div>
                    }

                </div>

            </div>
        )
    }

    /* The kinds of the payments — each one filters the list */
    _kinds = () => {

        /* Fields */
        const { kinds, kind } = this.state

        if (kinds.length === 0) {
            return null
        }

        const total = kinds.reduce((sum, item) => sum + item.amount, 0)

        return (
            <div className="transaction-kinds">
                {kinds.map(item => {

                    const meta = kindOf(item.kind)

                    return (
                        <div key={item.kind} onClick={() => this.toggle("kind", item.kind, "")} className={`transaction-kind ${meta.accent} ${kind === item.kind ? "active" : ""}`} title={meta.hint}>
                            <div className={`transaction-dot ${meta.accent}`}><Icon name={meta.icon} size={12} width={2.2} /></div>
                            <div className="transaction-kind-text">
                                <span>{meta.text} · {item.count}</span>
                                <b>{money(item.amount)}</b>
                            </div>
                            <em>{total > 0 ? Math.round(item.amount / total * 100) : 0}%</em>
                        </div>
                    )
                })}
            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { source, status, kind, sort, count, sum } = this.state

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {SOURCES.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ source: item.key })} className={source === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

                {status &&
                    <div className="service-filter active">
                        <Icon name={statusOf(status).icon} size={14} width={2.2} />
                        {statusOf(status).text}
                        <div onClick={() => this.refilter({ status: "" })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                {kind && KINDS[kind] &&
                    <div className="service-filter active">
                        <Icon name={kindOf(kind).icon} size={14} width={2.2} />
                        {kindOf(kind).text}
                        <div onClick={() => this.refilter({ kind: "" })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                <div className="user-count">{count.toLocaleString("ru-RU")} {plural(count, "платёж", "платежа", "платежей")}{sum > 0 ? ` · ${money(sum)}` : ""}</div>

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    {SORTS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

            </div>
        )
    }

    /* One payment of the list */
    _item = item => {

        const kind = kindOf(item.kind)
        const status = statusOf(item.status)
        const card = cardOf(item.cardPan)

        let title = item.organization ? item.organization : "Точка удалена"
        let details = [kind.text]

        if (item.source === "certificate") {
            title = item.recipient ? `Сертификат для ${item.recipient}` : "Подарочный сертификат"
            details = [item.person ? `купил ${item.person}` : "покупатель без имени"]
            if (item.personPhone) {
                details.push(item.personPhone)
            }
        }
        else {
            if (item.plan && PLANS[item.plan]) {
                details.push(PLANS[item.plan].toLowerCase())
            }
            if (card) {
                details.push(card)
            }
        }

        return (
            <div key={item.key} onClick={() => this._transaction.current.open(item)} className={`transaction-item ${item.status}`}>

                <div className="transaction-item-icon">
                    {item.source === "subscription" && item.image
                        ? <Avatar className="transaction-item-avatar" src={`${env.mediapoint}organizations/${item.image}`} alt={title}>{organizationIcon()}</Avatar>
                        : <div className={`transaction-icon ${kind.accent}`}><Icon name={kind.icon} size={18} width={2} /></div>
                    }
                    {item.source === "subscription" && item.image && <div className={`transaction-item-badge ${kind.accent}`}><Icon name={kind.icon} size={9} width={2.6} /></div>}
                </div>

                <div className="transaction-item-text">
                    <b>{title}</b>
                    <span>{details.join(" · ")}</span>
                </div>

                <div className="transaction-item-status">
                    <div className={`user-badge ${status.accent}`}><Icon name={status.icon} size={11} width={2.6} />{status.text}</div>
                </div>

                <div className="transaction-item-time">{moment(item.date).format("HH:mm")}</div>

                <div className="transaction-item-amount">{item.status === "success" ? "+" : ""}{money(item.amount)}</div>

            </div>
        )
    }

    /* The list split by the days */
    _list = () => {

        /* Fields */
        const { data } = this.state

        const groups = []

        data.forEach(item => {

            const day = moment(item.date).format("YYYY-MM-DD")
            const last = groups[groups.length - 1]

            if (last && last.day === day) {
                last.items.push(item)
            }
            else {
                groups.push({ day, items: [item] })
            }
        })

        return groups.map(group => {

            const date = moment(group.day)
            const income = group.items.filter(item => item.status === "success").reduce((sum, item) => sum + item.amount, 0)

            let label = date.format("D MMMM, dddd")

            if (date.isSame(moment(), "day")) {
                label = "Сегодня"
            }
            else if (date.isSame(moment().subtract(1, "day"), "day")) {
                label = "Вчера"
            }

            if (!date.isSame(moment(), "year")) {
                label = date.format("D MMMM YYYY")
            }

            return (
                <div className="transaction-day" key={group.day}>
                    <div className="transaction-day-head">
                        <b>{label}</b>
                        {income > 0 && <span>+{money(income)}</span>}
                    </div>
                    {group.items.map(item => this._item(item))}
                </div>
            )
        })
    }

    _content = () => {

        /* Fields */
        const { status, data, count, page, limit } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._periods()}
                    {this._summary()}
                    {this._kinds()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ ...INITIAL, period: this.state.period })} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._periods()}
                {this._summary()}
                {this._kinds()}
                {this._toolbar()}

                <div className="service-list transaction-list">
                    {this._list()}
                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { navigate } = this.props

        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <TransactionModal
                    ref={this._transaction}
                    onOrganization={id => navigate(`/organization?open=${id}`)}
                    onClient={user => this._user.current.open({ ...user, active: 1 })}
                />

                <UserModal ref={this._user} onChanged={() => { }} />

            </div>
        )
    }
}

export default withRouter(Transactions)
